import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { DetalleVenta, Mercaderia, Usuario } from 'src/app/model/index.model';
import { UturuncoUtils } from 'src/app/utils/uturuncoUtils';
import Swal from 'sweetalert2';
import { v4 as uuidv4 } from 'uuid';

@Injectable()
export class WorkshoppingService {
  cantidad: number = 1;
  precio: number = 0;
  mercaderia: Mercaderia | null = null;
  detalle: DetalleVenta[] = [];
  condicionVenta: 'contado' | 'debito' | 'tarjeta' | 'mayorista' = 'tarjeta';

  private cantidadSubject = new BehaviorSubject<number>(1); // Observable para la cantidad
  cantidad$ = this.cantidadSubject.asObservable(); // Exponer como observable para la vista

  private clienteID: string | null = null;
  private carritoKey: string | null = null;
  private readonly EXPIRATION_DAYS = 7;

  constructor() {
    //this.registrarCliente();
    //  this.cargarCarrito();
  }

  /** Registra un cliente único y configura las claves de almacenamiento */
  public registrarCliente(): void {
    this.clienteID = this.getFromLocalStorage('clienteID');
    if (!this.clienteID) {
      this.clienteID = uuidv4();
      this.saveToLocalStorage('clienteID', this.clienteID, null);
    }
    this.carritoKey = `carrito_${this.clienteID}`;
  }

  /** Guarda el carrito en localStorage con expiración */
  private guardarCarrito(): void {
    if (this.carritoKey) {
      const carrito = { detalle: this.detalle, condicion: this.condicionVenta };
      this.saveToLocalStorage(this.carritoKey, carrito, this.EXPIRATION_DAYS);
    }
  }

  /** Carga el carrito desde localStorage */
  public cargarCarrito(): void {
    if (this.carritoKey) {
      const carrito: { detalle: DetalleVenta[]; condicion: 'tarjeta' } =
        this.getFromLocalStorage(this.carritoKey) || {
          detalle: [],
          condicion: 'tarjeta',
        };
      this.detalle = carrito.detalle;
      this.condicionVenta = carrito.condicion;
      this.setCantidad(this.getCantidadTotal());
    }
  }

  /** Limpia el carrito después de completar la compra */
  limpiarCarrito(): void {
    if (this.carritoKey) {
      UturuncoUtils.removeItemSession(this.carritoKey);
    }
  }

  /** Selecciona un producto y calcula el precio según la condición de venta */
  async itemSel(event: Mercaderia) {
    if (!event.id) {
      UturuncoUtils.showToas('No se encontró la mercadería', 'info');
      return;
    }

    this.mercaderia = event;

    // Calcula el precio según condición y rol del usuario
    this.precio = this.getPrecioPorCondicion(
      event,
      this.condicionVenta,
      this.getUsuarioActivo(),
      this.cantidad
    );

    await this.addRow();
  }

  /** Calcula el precio según la condición de venta y el rol del usuario */
  private getPrecioPorCondicion(
    mercaderia: Mercaderia,
    condicionVenta: string,
    usuario: any,
    cantidad: number = 1
  ): number {
    const esAdmin = usuario?.rol?.id == 1;
    const esClienteWeb = usuario?.rol?.id == 3;
    const superaOferta = cantidad >= (mercaderia.cantOferta);

    console.log("por que no anda", usuario, superaOferta, condicionVenta)

    // Caso 1️⃣: Admin desde el panel con condición contado → precio mayorista
    if (esAdmin && condicionVenta === 'mayorista') {
      return parseFloat("" + mercaderia.precioMay);
    }
    // Caso 2️⃣: Cliente web (rol 3) con condición efectivo y cantidad suficiente
    if (esClienteWeb && condicionVenta === 'contado' && superaOferta) {
      return parseFloat("" + mercaderia.precioMay);
    }

    // Caso general
    switch (condicionVenta) {
      case 'mayorista':
        return parseFloat("" + mercaderia.precioMay);
      case 'contado':
        return parseFloat("" + mercaderia.precioCon);
      case 'debito':
        return parseFloat("" + mercaderia.precioDeb);
      default:
        return parseFloat("" + mercaderia.precioLis);
    }
  }

  /** Agrega un producto al carrito */
  async addRow(): Promise<void> {
    if (!this.isValidInput()) return;

    let precioFinal = this.precio;

    if (precioFinal <= 0) {
      const result = await this.requestPrice();
      if (!result.success) return;  // asumiendo que requestPrice devuelve {success, precio}
      this.precio = result.precio!;
    }

    // ... resto de validaciones de stock ...
    if (this.mercaderia.stock < this.cantidad) {
      UturuncoUtils.showToas('No contamos con la cantidad solicitada de este producto', 'error');
      return;
    }

    if (!this.updateExistingItem()) {
      this.addNewItem();  // donde ya usás this.precio (que ahora es el manual si se pidió)
    }

    this.guardarCarrito();
    this.resetInputs();
    this.setCantidad(this.getCantidadTotal());
  }


  getCantidadTotal(): number {
    return this.detalle.reduce((total, item) => total + item.cantidad, 0);
  }
  /** Valida los datos de entrada */
  private isValidInput(): boolean {
    console.log("wsdl", this.cantidad)
    if (!this.mercaderia?.id || this.cantidad < -1) {
      UturuncoUtils.showToas(
        'Debe seleccionar una mercadería y agregar una cantidad',
        'error'
      );
      return false;
    }
    return true;
  }

  /** Solicita un precio al usuario */
  private async requestPrice(): Promise<{ success: boolean; precio?: number }> {
    const minValue = this.mercaderia?.precioCom ?? 0;  // fallback si es undefined
    const res = await Swal.fire({
      title: 'Ingrese importe de la venta mayor a ' + this.mercaderia!.precioCom,
      input: 'number',
      inputAttributes: {
        autocapitalize: 'off',
        min: minValue.toString(),          // siempre string
        step: '0.01'                       // opcional: permite decimales si vendés con centavos
      }, confirmButtonText: 'Aceptar',
      showCancelButton: true,
    });

    if (res.isDismissed) return { success: false };

    const nuevoPrecio = parseFloat(res.value as string || '0');
    if (nuevoPrecio > this.mercaderia!.precioCom) {
      return { success: true, precio: nuevoPrecio };
    }

    UturuncoUtils.showToas('Debe ingresar un precio mayor al de compra', 'warning');
    return { success: false };
  }

  /** Actualiza un producto existente en el carrito */
  private updateExistingItem(): boolean {
    let itemUpdated = false;

    this.detalle = this.detalle.map((e) => {
      if (e.mercaderia.id === this.mercaderia!.id) {
        itemUpdated = true;
        const nuevaCantidad = e.cantidad + this.cantidad;

        if (this.mercaderia!.stock < nuevaCantidad) {
          UturuncoUtils.showToas('No contamos con la cantidad solicitada de este producto', 'error');
          return e;
        }

        // Precio sugerido con nueva cantidad
        const precioSugerido = this.getPrecioPorCondicion(
          this.mercaderia!,
          this.condicionVenta,
          this.getUsuarioActivo(),
          nuevaCantidad
        );

        // Usar sugerido solo si > 0, sino mantener el que ya tenía
        const precioFinal = precioSugerido > 0 ? precioSugerido : e.precio;
        const gananciaFinal = this.calculateGanancia(precioFinal);

        return {
          ...e,
          cantidad: nuevaCantidad,
          precio: precioFinal,
          ganancia: gananciaFinal,
        };
      }
      return e;
    });

    return itemUpdated;
  }

  /** Agrega un nuevo producto al carrito */
  private addNewItem(): void {
    const nuevoDetalle = this.createDetalleVenta();
    this.detalle.unshift(nuevoDetalle);
    console.log(this.detalle, "Que va agregar", nuevoDetalle)

  }

  /** Crea un nuevo detalle de venta */
  private createDetalleVenta(): DetalleVenta {
    return {
      cantidad: this.cantidad,
      mercaderia: this.mercaderia!,
      precio: this.precio,
      ganancia: this.calculateGanancia(this.precio),
      usuario: this.getUsuarioActivo(),
      descripcion: this.mercaderia!.grupo.categoria.nombre,
    } as DetalleVenta;
  }

  /** Calcula la ganancia de un producto */
  public calculateTotal(): number {
    return parseFloat(this.detalle.reduce((total, item) => total + (item.cantidad * item.precio), 0) + "");
  }

  /** Calcula la ganancia de un producto */
  private calculateGanancia(precio: number): number {
    return precio - parseFloat(this.mercaderia!.precioCom + "");
  }

  /** Obtiene el usuario actual desde la sesión */
  private getCurrentUser(): Usuario {
    return JSON.parse(UturuncoUtils.getSession('user'));
  }
  /** Obtiene el usuario actual (sistema o comprador web) */
  private getUsuarioActivo(): any {
    // Si existe el usuario del sistema
    const usuarioSistema = this.getCurrentUser();
    // Si existe un usuario del carrito web
    const usuarioCarrito = JSON.parse(UturuncoUtils.getSession(UturuncoUtils.UC))

    // Prioridad: si hay usuario del carrito, usar ese
    return usuarioCarrito || usuarioSistema || {};
  }
  /** Restablece los valores de entrada */
  private resetInputs(): void {
    this.mercaderia = null;
    this.cantidad = 1;
  }

  /** Guarda un objeto en localStorage con una posible fecha de expiración */
  private saveToLocalStorage(
    key: string,
    value: any,
    daysToExpire: number | null
  ): void {
    const expiration = daysToExpire
      ? new Date().getTime() + daysToExpire * 24 * 60 * 60 * 1000
      : null;
    const item = { value, expiration };
    UturuncoUtils.setSession(key, JSON.stringify(item));
  }

  /** Obtiene un objeto de localStorage verificando su expiración */
  private getFromLocalStorage<T>(key: string): T | null {
    const itemStr = UturuncoUtils.getSession(key);
    if (!itemStr) return null;

    const item = JSON.parse(itemStr);
    if (item.expiration && new Date().getTime() > item.expiration) {
      UturuncoUtils.removeItemSession(key);
      return null;
    }
    return item.value;
  }

  /** Actualiza la cantidad y notifica a los suscriptores */
  private setCantidad(nuevaCantidad: number): void {
    if (nuevaCantidad >= 0) {
      this.cantidadSubject.next(nuevaCantidad);
    } else {
      UturuncoUtils.showToas('La cantidad no puede ser negativa', 'error');
    }
  }

  /** Actualiza todos los productos del carrito según la nueva condición de venta */
  public actualizarCarritoPorCondicion(condicion: 'contado' | 'debito' | 'tarjeta' | 'mayorista'): void {
    this.condicionVenta = condicion;

    this.detalle = this.detalle.map((item) => {
      // Calculamos lo que el sistema sugeriría ahora

      const precioSugerido = this.getPrecioPorCondicion(
        item.mercaderia,
        condicion,
        this.getUsuarioActivo(),
        item.cantidad
      );

      // Si el sistema devuelve un precio válido (> 0), lo usamos
      // Si no (0 o falsy), mantenemos el precio que ya tenía el ítem
      const precioFinal = precioSugerido > 0 ? precioSugerido : item.precio;
      this.mercaderia = item.mercaderia;
      return {
        ...item,
        precio: precioFinal,
        ganancia: this.calculateGanancia(precioFinal),
      };
    });

    this.guardarCarrito();
    UturuncoUtils.showToas('Carrito actualizado con la nueva condición de venta', 'success');
  }

  /** Elimina un producto del carrito por su ID */
  public eliminarDelCarrito(itemR: Mercaderia): void {
    // Filtrar los elementos manteniendo solo los que no coincidan con el ID proporcionado
    const nuevoDetalle = this.detalle.filter((item) => item.mercaderia.id !== itemR.id);

    // Validar si realmente se eliminó algo
    if (nuevoDetalle.length === this.detalle.length) {
      UturuncoUtils.showToas('El producto no se encuentra en el carrito', 'info');
      return;
    }

    // Actualizar el detalle del carrito
    this.detalle = nuevoDetalle;

    // Guardar cambios en localStorage
    this.guardarCarrito();

    // Actualizar la cantidad total en el observable
    this.setCantidad(this.getCantidadTotal());

  }

  /** Resta la cantidad de un producto en el carrito y lo elimina si la cantidad es 0 */
  public restarCantidadDelCarrito(itemDes: Mercaderia): void {
    this.detalle = this.detalle.map((item) => {
      if (item.mercaderia.id === itemDes.id) {
        const nuevaCantidad = item.cantidad - 1;
        if (nuevaCantidad <= 0) return null;

        // Precio sugerido con la nueva cantidad
        const precioSugerido = this.getPrecioPorCondicion(
          item.mercaderia,
          this.condicionVenta,
          this.getUsuarioActivo(),
          nuevaCantidad
        );

        // Usar sugerido solo si es > 0, sino mantener el actual
        const precioFinal = precioSugerido > 0 ? precioSugerido : item.precio;
        this.mercaderia = item.mercaderia;
        return {
          ...item,
          cantidad: nuevaCantidad,
          precio: precioFinal,
          ganancia: this.calculateGanancia(precioFinal),
        };
      }
      return item;
    }).filter((item): item is DetalleVenta => item !== null);

    this.guardarCarrito();
    this.setCantidad(this.getCantidadTotal());
  }
}
